import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from './ToastProvider';
import { updateAgenda, moveAgendaStatus, deleteAgenda, createAgenda } from '@/features/agenda/services/agenda.api';
import type { AgendaItemDto, AgendaStatus, AgendaType } from '@/features/agenda/types';
import { listArtists } from '@/features/artists/services/artists.api';

interface Props {
  open: boolean;
  onClose: () => void;
  item: AgendaItemDto;
  onSaved: (it: AgendaItemDto) => Promise<void> | void;
}

const profileSections: Record<string, { contacts?: boolean; finance?: boolean; ops?: boolean; risks?: boolean; impact?: boolean; decisions?: boolean }> = {
  Executivo: { contacts: true, finance: true, ops: false, risks: true, impact: true, decisions: true },
  Artista: { contacts: true, finance: false, ops: false, risks: false, impact: true, decisions: true },
  Produção: { contacts: true, finance: false, ops: true, risks: true, impact: false, decisions: true },
  Jurídico: { contacts: false, finance: false, ops: false, risks: true, impact: false, decisions: true },
  Press: { contacts: true, finance: false, ops: false, risks: false, impact: true, decisions: true },
};

const checklistByType: Record<AgendaType, string[]> = {
  SHOW: ['Backline', 'Contrato', 'Transporte', 'Hospitality'],
  TOUR: ['Routing', 'Vistos', 'Seguros', 'Budget'],
  COLLAB: ['Estúdio', 'Rider', 'Direitos'],
  PROMO: ['Roteiro', 'Mídia', 'Press Kit'],
  OPERATIONS: ['Briefing', 'Materiais', 'Aprovações'],
  NETWORKING: ['Agenda', 'Convites', 'Follow-up'],
};

export const AgendaDetailDialog: React.FC<Props> = ({ open, onClose, item, onSaved }) => {
  const toast = useToast();
  const [tab, setTab] = useState<'detalhes'|'execucao'|'relatorio'>('detalhes');
  const [profile, setProfile] = useState<keyof typeof profileSections>('Executivo');
  const [draft, setDraft] = useState<AgendaItemDto>(item);
  const [artistQuery, setArtistQuery] = useState('');
  const [artistOptions, setArtistOptions] = useState<{id:string;name:string}[]>([]);

  useEffect(()=>{ setDraft(item); setTab('detalhes'); }, [item, open]);

  useEffect(()=>{
    let active = true;
    (async()=>{
      if (!artistQuery.trim()) { setArtistOptions([]); return; }
      const res = await listArtists(0, 10, { q: artistQuery.trim() });
      if (active) setArtistOptions(res.items.map(a=>({id:a.id, name:a.name})));
    })();
    return ()=> { active=false; };
  }, [artistQuery]);

  const sections = profileSections[profile] || {};
  const typeChecklist = checklistByType[draft.type as AgendaType] || [];

  const save = async () => {
    try { const updated = await updateAgenda(draft.id, draft); await onSaved(updated); toast.success('Compromisso atualizado'); onClose(); }
    catch (e:any) { toast.error('Falha ao salvar', e?.message); }
  };

  const remove = async () => { try { await deleteAgenda(draft.id); toast.success('Compromisso excluído'); onClose(); } catch(e:any){ toast.error('Falha ao excluir', e?.message);} };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-3xl sm:rounded-2xl bg-white dark:bg-zinc-900 border border-default sm:p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-default">
          <div>
            <h2 className="text-lg font-bold">{draft.title || `${draft.type}${draft.locationName? ' – '+draft.locationName:''}`}</h2>
            <p className="text-xs text-muted">{draft.startAt?.slice(0,16) || '-'} — Status: {draft.status}</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={profile} onChange={e=>setProfile(e.target.value as any)} className="px-2 py-1 rounded-md border border-default bg-transparent text-xs">
              {Object.keys(profileSections).map(p=> <option key={p}>{p}</option>)}
            </select>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200 p-1"><i className="ph ph-x text-xl"/></button>
          </div>
        </div>

        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 bg-zinc-200 dark:bg-zinc-800 p-1 rounded-full w-fit mb-3">
            {['detalhes','execucao','relatorio'].map(t => (
              <button key={t} onClick={()=>setTab(t as any)} className={`px-3 py-1.5 text-sm font-semibold rounded-full ${tab===t? 'bg-green-500 text-black':'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-700'}`}>{t==='detalhes'? 'Detalhes': t==='execucao'? 'Execução':'Relatório'}</button>
            ))}
          </div>

          {tab==='detalhes' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-muted">Título</span>
                  <input value={draft.title || ''} onChange={e=>setDraft({...draft, title: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block"><span className="text-xs text-muted">Motivo</span>
                  <input value={draft.motive || ''} onChange={e=>setDraft({...draft, motive: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block"><span className="text-xs text-muted">Tipo</span>
                  <select value={draft.type} onChange={e=>setDraft({...draft, type: e.target.value as any})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent">
                    <option>SHOW</option><option>TOUR</option><option>COLLAB</option><option>PROMO</option><option>OPERATIONS</option><option>NETWORKING</option>
                  </select>
                </label>
                <label className="block"><span className="text-xs text-muted">Status</span>
                  <select value={draft.status} onChange={e=>setDraft({...draft, status: e.target.value as any})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent">
                    <option>PROSPECCAO</option><option>HOLD</option><option>NEGOCIACAO</option><option>CONFIRMADO</option><option>ENTREGUE</option>
                  </select>
                </label>
                <label className="block"><span className="text-xs text-muted">Início</span>
                  <input type="datetime-local" value={draft.startAt? draft.startAt.slice(0,16): ''} onChange={e=>setDraft({...(draft as any), startAt: e.target.value? new Date(e.target.value).toISOString(): undefined} as any)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block"><span className="text-xs text-muted">Fim</span>
                  <input type="datetime-local" value={draft.endAt? draft.endAt.slice(0,16): ''} onChange={e=>setDraft({...(draft as any), endAt: e.target.value? new Date(e.target.value).toISOString(): undefined} as any)} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
                <label className="block md:col-span-2"><span className="text-xs text-muted">Local</span>
                  <input value={draft.locationName || ''} onChange={e=>setDraft({...draft, locationName: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                </label>
              </div>

              {/* Seleção de artista */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-muted">Vincular artista</span>
                  <input value={artistQuery} onChange={e=>setArtistQuery(e.target.value)} placeholder="Buscar artista pelo nome..." className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                  {artistOptions.length>0 && (
                    <div className="mt-1 bg-surface border border-default rounded-md max-h-40 overflow-auto">
                      {artistOptions.map(o => (
                        <button key={o.id} type="button" onClick={()=>{ setDraft({...draft, artistId: o.id}); setArtistQuery(o.name); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/10">
                          {o.name}
                        </button>
                      ))}
                    </div>
                  )}
                </label>
                <div className="flex items-end gap-2">
                  <button onClick={()=> moveAgendaStatus(draft.id, 'CONFIRMADO').then(()=>toast.success('Confirmado')).catch(e=>toast.error('Falha', e?.message))} className="px-3 py-2 rounded-md border border-default text-sm">Confirmar</button>
                  <button onClick={()=> moveAgendaStatus(draft.id, 'NEGOCIACAO').then(()=>toast.success('Em negociação')).catch(e=>toast.error('Falha', e?.message))} className="px-3 py-2 rounded-md border border-default text-sm">Negociação</button>
                </div>
              </div>

              {/* Perfis: seções opcionais */}
              {sections && sections.finance && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="block"><span className="text-xs text-muted">Fee (centavos)</span>
                    <input type="number" value={draft.feeCents ?? 0} onChange={e=>setDraft({...draft, feeCents: Number(e.target.value)||0})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                  </label>
                  <label className="block"><span className="text-xs text-muted">Custos (centavos)</span>
                    <input type="number" value={draft.costsCents ?? 0} onChange={e=>setDraft({...draft, costsCents: Number(e.target.value)||0})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                  </label>
                  <label className="block"><span className="text-xs text-muted">Margem (centavos)</span>
                    <input type="number" value={draft.marginCents ?? 0} onChange={e=>setDraft({...draft, marginCents: Number(e.target.value)||0})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent" />
                  </label>
                </div>
              )}

              {sections && sections.ops && (
                <div className="p-3 rounded-md bg-subtle border border-default">
                  <p className="text-xs text-muted mb-2">Checklist ({draft.type})</p>
                  <div className="flex flex-wrap gap-2">
                    {typeChecklist.map((c,i)=> (
                      <label key={i} className="inline-flex items-center gap-2 text-sm"><input type="checkbox" className="rounded" /> {c}</label>
                    ))}
                  </div>
                </div>
              )}

              {sections && sections.contacts && (
                <div className="p-3 rounded-md bg-subtle border border-default">
                  <p className="text-xs text-muted mb-2">Contatos</p>
                  <p className="text-xs text-muted">(em breve: formulário dinâmico com papéis e contatos)</p>
                </div>
              )}

              {sections && sections.risks && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs text-muted">Risco</span>
                    <select value={draft.riskFlag? '1':'0'} onChange={e=>setDraft({...draft, riskFlag: e.target.value==='1'})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent">
                      <option value="0">Sem risco</option>
                      <option value="1">Com risco</option>
                    </select>
                  </label>
                  <label className="block md:col-span-2"><span className="text-xs text-muted">Notas de risco</span>
                    <textarea value={draft.riskNotes || ''} onChange={e=>setDraft({...draft, riskNotes: e.target.value})} className="mt-1 w-full px-3 py-2 rounded-md border border-default bg-transparent min-h-[60px]" />
                  </label>
                </div>
              )}

              {sections && sections.impact && (
                <div className="p-3 rounded-md bg-subtle border border-default">
                  <p className="text-xs text-muted mb-2">Impacto esperado</p>
                  <p className="text-xs text-muted">(em breve: público, mídia, lift em seguidores, networking)</p>
                </div>
              )}

              {sections && sections.decisions && (
                <div className="p-3 rounded-md bg-subtle border border-default">
                  <p className="text-xs text-muted mb-2">Decisões pendentes</p>
                  <p className="text-xs text-muted">(em breve: até 3 decisões prioritárias)</p>
                </div>
              )}
            </div>
          )}

          {tab==='execucao' && (
            <div className="p-3 rounded-md bg-subtle border border-default text-sm text-muted">Modo Execução: timeline do dia, contatos-chave e checklist (breve).</div>
          )}

          {tab==='relatorio' && (
            <div className="p-3 rounded-md bg-subtle border border-default text-sm text-muted">Relatório: previsto vs. realizado (financeiro e impacto) – em breve.</div>
          )}

          <div className="flex items-center justify-between py-4">
            <button onClick={remove} className="px-3 py-2 rounded-md border border-default text-red-600">Excluir</button>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="px-3 py-2 rounded-md border border-default">Cancelar</button>
              <button onClick={save} className="px-3 py-2 rounded-md bg-green-600 text-white">Salvar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgendaDetailDialog;
